import { DEFAULT_FOCUS_AREAS } from '../../domain/constants/aiAuditorSettings';
import { isSessionActivityAuditableAtCheckout } from '../../domain/constants/auditableSessionActivityStatuses';
import {
  isNoteChangedSinceAudit as resolveNoteChanged,
  isStoredAuditOutdated,
} from '../../domain/services/auditFreshness';
import {
  getStoredAuditRunCount,
  isAuditRunInFlight,
  isLocalAuditRunSuperseded,
} from '../../domain/services/auditRunMarker';
import { isAuditorEnabledForTemplate, resolveTemplateStrictness } from '../../domain/services/auditorTemplateGate';
import {
  hasAuditResult,
  isRerunOffered,
  resolveCheckoutAuditStatus,
} from '../../domain/services/checkoutAuditStatus';

/** @typedef {import('../../domain/constants/checkoutAuditStatuses').CheckoutAuditStatus} CheckoutAuditStatus */

const NO_KNOWN_RUN = Object.freeze({
  verdict: null,
  auditedNotes: null,
  baselineNotes: null,
  sessionActivityAuditId: null,
  auditRunCount: null,
  failureReason: null,
});

/**
 * The check-out auditor's decisions: whether this note may be reviewed, whether arriving at the
 * summary step earns a run, what the surface is showing, and whether the written result describes
 * the run in hand.
 *
 * The run state is module scoped rather than held here: two surfaces can show one note at once, and
 * the wizard asks the same questions the card does without rendering it.
 *
 * @param {Object} deps
 * @param {() => Object|null} deps.getSessionActivity
 * @param {() => Object|null} deps.getSessionNoteTemplate
 * @param {() => string} deps.getNotes
 * @param {() => boolean} deps.getIsSuspended - Contexts that must never audit, e.g. a template preview.
 * @param {() => boolean} deps.getIsOffline
 * @param {() => number} deps.getNow - Wall clock, so the in-flight marker's age can expire on screen.
 * @param {() => boolean} deps.getCanRunAudit
 * @param {Object} deps.runState - useAuditRunState()'s shape.
 * @param {{ run: Function }} deps.audit
 * @param {{ trackRun: Function, trackNoteSigned: Function }} deps.analytics
 */
// eslint-disable-next-line import/prefer-default-export
export function createCheckoutAuditor({
  getSessionActivity,
  getSessionNoteTemplate,
  getNotes,
  getIsSuspended = () => false,
  getIsOffline,
  getNow = () => Date.now(),
  getCanRunAudit,
  runState,
  audit,
  analytics,
}) {
  const auditRun = () => runState.getAuditRun(getSessionActivity()?.id);

  const isSuperseded = () => isLocalAuditRunSuperseded(auditRun(), getSessionActivity());

  // What this client knows about the stored result, dropped once a later run replaced that result.
  const knownRun = () => (isSuperseded() ? NO_KNOWN_RUN : auditRun());

  // Ignoring is not enough: the stale baseline would block a new one, and a new run would revive it.
  const forgetIfSuperseded = () => {
    if (isSuperseded()) {
      runState.forgetAuditRun(getSessionActivity()?.id);
    }
  };

  const isRunning = () => isAuditRunInFlight(auditRun(), getSessionActivity(), getNow());

  const isEnabled = () => {
    const sessionActivity = getSessionActivity();

    return getCanRunAudit()
      && !getIsSuspended()
      && isAuditorEnabledForTemplate(getSessionNoteTemplate())
      && isSessionActivityAuditableAtCheckout(sessionActivity)
      && !!sessionActivity?.clinic;
  };

  // A note reviewed before this page loaded still carries its verdict.
  const verdict = () => knownRun().verdict ?? getSessionActivity()?.audit?.verdict ?? null;

  const isNoteChangedSinceAudit = () => resolveNoteChanged({
    currentNotes: getNotes(),
    auditedNotes: knownRun().auditedNotes,
    baselineNotes: knownRun().baselineNotes,
    isStoredOutdated: isStoredAuditOutdated(getSessionActivity()),
  });

  const status = () => resolveCheckoutAuditStatus({
    isRunning: isRunning(),
    verdict: verdict(),
    failureReason: knownRun().failureReason,
    isNoteChangedSinceAudit: isNoteChangedSinceAudit(),
    isOffline: getIsOffline(),
  });

  // On a re-run the activity holds the previous run's wording until the snapshot lands.
  const isStoredResultFromThisRun = () => {
    const { sessionActivityAuditId } = knownRun();

    return !sessionActivityAuditId
      || getSessionActivity()?.audit?.sessionActivityAuditId === sessionActivityAuditId;
  };

  // Arrives at the live-bound activity a moment after the verdict, so it would caption the wrong state.
  const rationale = () => (
    hasAuditResult(status()) && isStoredResultFromThisRun()
      ? getSessionActivity()?.audit?.rationale ?? null
      : null
  );

  const canRerun = () => isRerunOffered(status()) && !getIsOffline();

  async function runAudit(isRerun) {
    const sessionActivity = getSessionActivity();
    const notes = getNotes();

    if (isRunning() || !isEnabled() || !notes?.trim()) {
      return;
    }

    if (getIsOffline()) {
      return;
    }

    forgetIfSuperseded();

    const { attemptNumber, runToken } = runState.startAuditRun(sessionActivity.id, sessionActivity.status);

    /*
     * The backend resolves this from the template itself, so that clinics on an older build get the
     * setting too, and it is the backend's answer that reaches the model. Sent rather than omitted
     * so the request is not asserting a strictness this template never asked for.
     */
    const outcome = await audit.run({
      sessionActivity,
      notes,
      isRerun,
      settings: {
        focusAreas: DEFAULT_FOCUS_AREAS,
        strictness: resolveTemplateStrictness(getSessionNoteTemplate()),
      },
    });

    runState.finishAuditRun(sessionActivity.id, outcome, runToken);

    analytics.trackRun({
      sessionActivityId: sessionActivity.id,
      runNumber: outcome.auditRunCount ?? attemptNumber,
      isRerun,
      verdict: outcome.verdict,
      failureReason: outcome.failureReason,
      sessionActivityAuditId: outcome.sessionActivityAuditId,
      notesHash: outcome.notesHash,
      flagResolution: outcome.flagResolution,
    });
  } // runAudit()

  /**
   * Safe to call on every arrival at the summary step. A verdict the note has outgrown does not
   * earn a fresh review: replacing it is a re-run.
   */
  function start() {
    const sessionActivity = getSessionActivity();

    forgetIfSuperseded();

    if (verdict()) {
      runState.rememberBaselineNotes(sessionActivity?.id, getNotes(), getStoredAuditRunCount(sessionActivity));
      return Promise.resolve();
    }

    return runAudit(false);
  } // start()

  function rerun() {
    if (!canRerun()) {
      return Promise.resolve();
    }

    return runAudit(true);
  } // rerun()

  function reportNoteSigned() {
    const sessionActivity = getSessionActivity();

    if (!isEnabled() || !sessionActivity) {
      return;
    }

    analytics.trackNoteSigned({
      sessionActivityId: sessionActivity.id,
      auditStatus: status(),
      wasEditedSinceAudit: isNoteChangedSinceAudit(),
      runNumber: knownRun().auditRunCount ?? sessionActivity.auditMeta?.auditRunCount ?? null,
    });
  } // reportNoteSigned()

  return {
    canRerun,
    isEnabled,
    isRunning,
    rationale,
    reportNoteSigned,
    rerun,
    start,
    status,
  };
}
